import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GroupableAccommodation } from '@lx/shared/data-access/products';
import { ButtonComponent } from '@lx/shared/ui/button';
import { PricePipe } from '@lx/shared/ui/pipes';
import { filter, Observable } from 'rxjs';
import { AccommodationsService } from '../../services';
import { QuoteToolLiteStore } from '../../store';
import { Accommodation, ACCOMMODATIONS_FALLBACK_IMAGE } from '../../types';
import { AccommodationDetailsComponent } from '../accommodation-details/accommodation-details.component';
import { AccommodationOptionsComponent } from '../accommodation-options/accommodation-options.component';
import { OptionCardComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { SidePanelGalleryComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { GoogleAnalyticsEventEnum } from '../../google-analytics';
import { GoogleAnalyticsClickEventDirective } from '@lx/shared/google-analytics';
import { MessageComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { SidePanelService } from '@lx/quote-tool/lite/shared/services';

@Component({
  standalone: true,
  selector: 'lx-accommodations',
  templateUrl: './accommodations.component.html',
  styleUrls: ['./accommodations.component.scss'],
  imports: [
    OptionCardComponent,
    CommonModule,
    PricePipe,
    ButtonComponent,
    AccommodationDetailsComponent,
    ButtonComponent,
    AccommodationOptionsComponent,
    SidePanelGalleryComponent,
    MessageComponent,
    GoogleAnalyticsClickEventDirective,
    MessageComponent,
  ],
})
export class AccommodationsComponent implements OnChanges {
  @Input() accommodations: GroupableAccommodation[];
  @Input() accommodationOptional: boolean | undefined;
  @Input() moreAccommodationsDisclaimerShown: boolean | undefined;

  @Output() accommodationSelected = new EventEmitter<Accommodation>();

  selectedAccommodation: Accommodation | undefined;
  detailsAccommodation: Accommodation | undefined;
  roomType: string | undefined;
  otherAccommodationOption: Accommodation | undefined;
  accommodationDetailsDialogVisible = false;
  selectedCurrency$: Observable<string> | undefined;
  galleryUrls: string[] = [];

  seeMoreEventName = GoogleAnalyticsEventEnum.QuoteAccommodationSeeMore;

  protected readonly ACCOMMODATIONS_FALLBACK_IMAGE = ACCOMMODATIONS_FALLBACK_IMAGE;

  private readonly accommodationsService = inject(AccommodationsService);
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly sidePanelService = inject(SidePanelService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.otherAccommodationOption = this.accommodationsService.getOtherAccommodationOption();
  }

  ngOnChanges({ accommodations }: SimpleChanges): void {
    this.selectedCurrency$ = this.quoteToolLiteStore.selectedCurrency$;
    if (accommodations.currentValue) {
      this.quoteToolLiteStore.selectedAccommodation$
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          filter(({ code }) => !!code),
        )
        .subscribe((selectedAccommodation) => this.setSelectedAccommodation(selectedAccommodation));
    }
  }

  setSelectedAccommodation(selectedAccommodation?: Accommodation | GroupableAccommodation | undefined): void {
    if (!selectedAccommodation?.code) {
      this.selectedAccommodation = this.otherAccommodationOption;
    } else {
      const accommodationFromAPI = this.accommodations.find((accommodation) => accommodation.code === selectedAccommodation.code);
      this.selectedAccommodation = accommodationFromAPI ? this.getAccommodation(accommodationFromAPI) : this.otherAccommodationOption;
    }

    this.accommodationSelected.emit(this.selectedAccommodation);
  }

  openAccommodationDetailsSidePanel(accommodation: Accommodation): void {
    this.galleryUrls = accommodation?.galleryUrls;
    this.accommodationDetailsDialogVisible = true;
    this.setDetailsAccommodation(accommodation);
  }

  hideAccommodationDetailsDialog() {
    this.sidePanelService.hidePanel(() => (this.accommodationDetailsDialogVisible = false));
  }

  private getAccommodation({
    accommodationType: type,
    code,
    name: title,
    thumbnailSmall: image,
    price,
    mealOptions,
    description,
    bathroomType,
    galleryUrls,
    hasPromotion,
    roomType,
    multiCurrencyPrice,
    groupedItems,
  }: GroupableAccommodation): Accommodation {
    return {
      type,
      code,
      title,
      image,
      price,
      description,
      mealOptions,
      bathroomType,
      galleryUrls,
      withPromotion: hasPromotion,
      roomType,
      multiCurrencyPrice,
      groupedItems,
    };
  }

  private setDetailsAccommodation(selectedAccommodation?: Accommodation | GroupableAccommodation): void {
    if (selectedAccommodation?.code) {
      const accommodationFromAPI = this.accommodations.find((accommodation) => accommodation.code === selectedAccommodation.code);
      this.detailsAccommodation = accommodationFromAPI ? this.getAccommodation(accommodationFromAPI) : this.otherAccommodationOption;
    }
  }
}
